import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {instance} from '../core';
import {NotificationPage, ReceiptNotification} from './notificationModel';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const isTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && Number.isFinite(Date.parse(value));

const isNotification = (value: unknown): value is ReceiptNotification =>
  isRecord(value) &&
  isId(value.id) &&
  isId(value.receiptId) &&
  typeof value.receiptName === 'string' &&
  typeof value.groupName === 'string' &&
  typeof value.actorName === 'string' &&
  isTimestamp(value.createdAt) &&
  (value.readAt === null || isTimestamp(value.readAt));

const isPage = (value: unknown): value is NotificationPage =>
  isRecord(value) &&
  Array.isArray(value.items) &&
  value.items.every(isNotification) &&
  typeof value.unreadCount === 'number' &&
  Number.isSafeInteger(value.unreadCount) &&
  value.unreadCount >= 0 &&
  (value.nextCursor === null || isId(value.nextCursor));

async function request(
  token: string,
  signal: AbortSignal,
  config: AxiosRequestConfig,
): Promise<AxiosResponse<unknown>> {
  const source = axios.CancelToken.source();
  const cancel = () => source.cancel('Notification request aborted');
  const adapter = instance.defaults.adapter!;
  signal.addEventListener('abort', cancel);
  if (signal.aborted) cancel();

  try {
    const response = await instance.request<unknown>({
      ...config,
      headers: {Authorization: `Bearer ${token}`},
      cancelToken: source.token,
      adapter: requestConfig => {
        // Axios 0.21 can merge differently cased default authorization headers.
        Object.keys(requestConfig.headers || {}).forEach(name => {
          if (name.toLowerCase() === 'authorization') delete requestConfig.headers[name];
        });
        requestConfig.headers.Authorization = `Bearer ${token}`;
        return adapter(requestConfig);
      },
    });
    source.token.throwIfRequested();
    return response;
  } finally {
    signal.removeEventListener('abort', cancel);
  }
}

export const notificationApi = {
  async list(token: string, signal: AbortSignal, before?: number): Promise<NotificationPage> {
    const response = await request(token, signal, {
      method: 'get',
      url: '/v1/notifications',
      params: {before, limit: 20},
    });
    if (!isPage(response.data)) throw new Error('Invalid notification page response');
    return response.data;
  },

  async markRead(token: string, signal: AbortSignal, id: number): Promise<ReceiptNotification> {
    const response = await request(token, signal, {
      method: 'post',
      url: `/v1/notifications/${id}/read`,
    });
    if (!isNotification(response.data)) throw new Error('Invalid notification response');
    return response.data;
  },

  async markAllRead(token: string, signal: AbortSignal, throughId: number): Promise<void> {
    const response = await request(token, signal, {
      method: 'post',
      url: '/v1/notifications/read-all',
      data: {throughId},
    });
    if (response.status !== 204) throw new Error('Invalid mark-all-read response');
  },
};
